import { ChangeEvent, ReactElement, useEffect, useState } from 'react';

import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import { CustomCellEditorProps } from 'ag-grid-react';

import { Box, InputAdornment, TextField, Typography } from '@mui/material';

import { formatDecimal, formatMoney } from '@octopus/formatters';
import dayjs from 'dayjs';

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    borderRadius: 0,
    '&:hover': {
      boxShadow: 'none',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(37, 37, 45, 0.10)',
      },
    },
    '&.Mui-focused': {
      boxShadow: 'none',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(37, 37, 45, 0.10)',
        borderRadius: 0,
      },
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0px solid rgba(37, 37, 45, 0.10)',
    borderRadius: 0,
  },
};

export type InputCellEditorProps = CustomCellEditorProps<unknown, string>;

const CURRENCY_REGEX = /^\d*(,\d?\d?)?$/;

export type CurrencyEditorProps = CustomCellEditorProps<
  unknown,
  string | number
> & {
  outputType?: 'string' | 'number';
};

export function CurrencyEditor(props: CurrencyEditorProps) {
  const maskito = useMaskito({
    options: {
      mask: CURRENCY_REGEX,
    },
  });
  const [value, setValue] = useState(() => {
    if (props.eventKey !== null && CURRENCY_REGEX.test(props.eventKey)) {
      return props.eventKey;
    }
    if (!props.initialValue) {
      return '';
    }
    return formatMoney(props.initialValue, { withCurrency: false });
  });
  const parseValue = () => {
    let parsedValue = value.replace(/\./g, '').replace(/,/g, '.');
    if (parsedValue.endsWith('.')) {
      parsedValue = parsedValue.slice(0, -1);
    }
    if (props.outputType === 'number') {
      return parseFloat(parsedValue);
    }
    return parsedValue;
  };
  useEffect(() => props.onValueChange(parseValue()), [value]);
  return (
    <CellEditor>
      <TextField
        fullWidth
        value={value}
        autoFocus
        onInput={(event: ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value)
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography variant="body2" color="strokes.heavy" mr={0.5}>
                R$
              </Typography>
            </InputAdornment>
          ),
          sx: {
            '& input': {
              px: 0,
              fontWeight: 500,
            },
          },
        }}
        ref={maskito}
        sx={{
          ...inputStyles,
          '& .MuiOutlinedInput-root': {
            ...inputStyles['& .MuiOutlinedInput-root'],
            padding: '0px 0px 0px 12px',
          },
        }}
      />
    </CellEditor>
  );
}

const NUMBER_REGEX = /^\d*(,\d?\d?\d?\d?\d?\d?)?$/;

export function NumberEditor(props: InputCellEditorProps) {
  const maskito = useMaskito({
    options: {
      mask: NUMBER_REGEX,
    },
  });
  const [value, setValue] = useState(() => {
    if (props.eventKey !== null && NUMBER_REGEX.test(props.eventKey)) {
      return props.eventKey;
    }
    if (!props.initialValue) {
      return '';
    }
    return formatMoney(props.initialValue, { withCurrency: false });
  });
  const parseValue = () => {
    const parsedValue = value.replace(/\./g, '').replace(/,/g, '.');
    if (parsedValue.endsWith('.')) {
      return parsedValue.slice(0, -1);
    }
    return parsedValue;
  };
  useEffect(() => props.onValueChange(parseValue()), [value]);
  return (
    <CellEditor>
      <TextField
        fullWidth
        value={value}
        autoFocus
        onInput={(event: ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value)
        }
        ref={maskito}
        sx={inputStyles}
      />
    </CellEditor>
  );
}

const HOURS_REGEX = /^\d*(:\d?\d?)?$/;

export function HoursEditor(props: InputCellEditorProps) {
  const maskito = useMaskito({
    options: {
      mask: HOURS_REGEX,
    },
  });
  const [value, setValue] = useState(() => {
    if (props.eventKey !== null && HOURS_REGEX.test(props.eventKey)) {
      return props.eventKey;
    }

    if (!props.initialValue) {
      return '';
    }
    const [hours, minutes] = props.initialValue.split(':');
    if (hours !== undefined) {
      return `${hours.padStart(2, '0')}:${minutes?.padEnd(2, '0') ?? '00'}`;
    }
    return '';
  });

  const parseValue = () => {
    if (!value.includes(':')) {
      return `${value}:00:00`;
    } else {
      if (value.endsWith(':')) {
        return `${value}00:00`;
      } else if (value.endsWith(':', value.length - 1)) {
        return `${value}0:00`;
      } else {
        return `${value}:00`;
      }
    }
  };

  useEffect(() => {
    props.onValueChange(parseValue());
  }, [value]);
  return (
    <CellEditor>
      <TextField
        fullWidth
        value={value}
        autoFocus
        onInput={(event: ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value)
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              <Typography variant="body2" color="strokes.heavy">
                h:m
              </Typography>
            </InputAdornment>
          ),
        }}
        ref={maskito}
        sx={inputStyles}
      />
    </CellEditor>
  );
}

const PERCENTAGE_REGEX = /^\d*(,\d*)?$/;

export function PercentageEditor(props: InputCellEditorProps) {
  const maskito = useMaskito({
    options: {
      mask: PERCENTAGE_REGEX,
    },
  });
  const [value, setValue] = useState(() => {
    if (props.eventKey !== null && PERCENTAGE_REGEX.test(props.eventKey)) {
      return props.eventKey;
    }
    if (!props.initialValue) {
      return '';
    }
    return formatDecimal(Number(props.initialValue) * 100);
  });

  const parseValue = () => {
    const parsedValue = value.replace(/\./g, '').replace(/,/g, '.');
    if (parsedValue.endsWith('.')) {
      return parsedValue.slice(0, -1);
    }
    return `${Number(parsedValue) / 100}`;
  };

  useEffect(() => props.onValueChange(parseValue()), [value]);
  return (
    <CellEditor>
      <TextField
        fullWidth={false}
        value={value}
        autoFocus
        onInput={(event: ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value)
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              <Typography variant="body2" color="strokes.heavy">
                %
              </Typography>
            </InputAdornment>
          ),
        }}
        ref={maskito}
        sx={inputStyles}
      />
    </CellEditor>
  );
}

export function DateEditor(props: InputCellEditorProps) {
  const [value, setValue] = useState(() => {
    if (props.eventKey && /^\d$/.test(props.eventKey)) {
      return props.eventKey;
    }
    if (!props.initialValue) {
      return '';
    }

    const date = dayjs(props.initialValue);
    if (date.isValid()) {
      return date.format('DD/MM/YYYY');
    }

    return props.initialValue;
  });

  const maskitoOptions = maskitoDateOptionsGenerator({
    mode: 'dd/mm/yyyy',
    separator: '/',
    max: new Date(2100, 11, 31),
  });

  const maskito = useMaskito({
    options: maskitoOptions,
  });

  const parseValue = () => {
    const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dateMatch) {
      return `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
    }
    return value;
  };

  useEffect(() => {
    props.onValueChange(parseValue());
  }, [value]);

  return (
    <CellEditor>
      <TextField
        value={value}
        autoFocus
        placeholder="dd/mm/aaaa"
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value)
        }
        onInput={(event: ChangeEvent<HTMLInputElement>) =>
          setValue(event.target.value)
        }
        inputProps={{
          ref: maskito,
          maxLength: 10,
        }}
        sx={inputStyles}
      />
    </CellEditor>
  );
}

function CellEditor({ children }: { children: ReactElement }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      width="100%"
      height="100%"
      boxSizing="border-box"
      overflow="hidden"
      borderRadius={0}
    >
      {children}
    </Box>
  );
}
